import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { AXES, COMPLIANCE_VIEWS, GRANULARITIES, TABS, type TimeStudyFilters } from './types';
import { firstDayOfMonthAfter, parseDay } from '@/lib/time';

// Filtros de Estudio de tiempos en la URL (UX-NAV-02, spec §4.2): un enlace compartido
// reproduce la misma vista. Un valor inválido se ignora y vale su defecto; el defecto
// no se escribe en la URL.

const ID = /^[1-9]\d{0,18}$/;

/** Lo que borra "Limpiar filtros" (G1-6): rango, tipo y proceso. Eje, vista del
 *  cumplimiento, granularidad y pestaña son la vista elegida, no un filtro: se conservan. */
const FILTER_KEYS = ['desde', 'hasta', 'tipo', 'area'] as const;

function oneOf<T extends string>(v: string | null, values: readonly T[], fallback: T): T {
  return values.find((x) => x === v) ?? fallback;
}

function defaults(today: string): TimeStudyFilters {
  return {
    desde: firstDayOfMonthAfter(today, 0),
    hasta: today,
    tipo: null,
    area: null,
    eje: 'person',
    cumpl: 'persona',
    gran: 'day',
    tab: 'analisis',
  };
}

export function parseFilters(p: URLSearchParams, today: string): TimeStudyFilters {
  const d = defaults(today);
  const id = (v: string | null): string | null => (v !== null && ID.test(v) ? v : null);
  return {
    desde: parseDay(p.get('desde')) ?? d.desde,
    hasta: parseDay(p.get('hasta')) ?? d.hasta,
    tipo: id(p.get('tipo')),
    area: id(p.get('area')),
    eje: oneOf(p.get('eje'), AXES, d.eje),
    cumpl: oneOf(p.get('cumpl'), COMPLIANCE_VIEWS, d.cumpl),
    gran: oneOf(p.get('gran'), GRANULARITIES, d.gran),
    tab: oneOf(p.get('tab'), TABS, d.tab),
  };
}

export interface TimeStudyUrlState {
  filters: TimeStudyFilters;
  setFilters: (patch: Partial<TimeStudyFilters>) => void;
  /** Algún filtro difiere de su valor por defecto. */
  hasFilters: boolean;
  clearFilters: () => void;
}

/** `today` es el día de negocio de Costa Rica (lo fija la pantalla). */
export function useTimeStudyUrlState(today: string): TimeStudyUrlState {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const filters = useMemo(() => parseFilters(new URLSearchParams(key), today), [key, today]);

  const setFilters = useCallback(
    (patch: Partial<TimeStudyFilters>) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const d = defaults(today);
          for (const [k, v] of Object.entries(patch) as [keyof TimeStudyFilters, string | null][]) {
            if (v === null || v === d[k]) {
              p.delete(k);
            } else {
              p.set(k, v);
            }
          }
          return p;
        },
        { replace: true },
      ),
    [setParams, today],
  );

  const d = defaults(today);
  const hasFilters = FILTER_KEYS.some((k) => filters[k] !== d[k]);
  // El defecto no se escribe en la URL: volver a él borra las claves.
  const clearFilters = useCallback(() => {
    const def = defaults(today);
    setFilters({ desde: def.desde, hasta: def.hasta, tipo: null, area: null });
  }, [setFilters, today]);

  return { filters, setFilters, hasFilters, clearFilters };
}
